import React, {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Alert, Box, Button, MenuItem, Modal, Paper, TextField} from '@mui/material';
import {PublicService} from '../../services/public.service';
import {InstitutionService} from "../../services/institution.service";
import {validateCodeFormat} from './institutionCode';
import { helpWhy } from '../../help/why';

interface CreateInstitutionProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    onSuccess?: (message?: string, email?: string) => void;
    selectedPlan?: string;
}

const NOT_SURE_YET = 'Not sure yet';

// Keep names in sync with the landing PricingSection plan cards.
const PLAN_OPTIONS = [
    { name: NOT_SURE_YET, label: 'Not sure yet' },
    { name: 'Free', label: 'Free (৳0, up to 50 employees)' },
    { name: 'Core HR', label: 'Core HR (৳15/employee/mo)' },
    { name: 'Business', label: 'Business (৳30/employee/mo)' },
    { name: 'Enterprise', label: 'Enterprise (custom pricing)' },
];

const CreateInstitution: React.FC<CreateInstitutionProps> = ({
    isModal = false,
    open = false,
    onClose,
    onSuccess,
    selectedPlan
}) => {
    const navigate = useNavigate();
    const [institution, setInstitution] = useState({ name: '', code: '', description: '', email: '' });
    const [plan, setPlan] = useState(selectedPlan || NOT_SURE_YET);

    useEffect(() => {
        if (open) setPlan(selectedPlan || NOT_SURE_YET);
    }, [open, selectedPlan]);
    const [error, setError] = useState('');
    const [codeError, setCodeError] = useState('');
    const [codeStatus, setCodeStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
    const [successMessage, setSuccessMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [setupCreating, setSetupCreating] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isCodeValid = codeStatus === 'available';

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
    }, []);

    const resetForm = () => {
        setInstitution({ name: '', code: '', description: '', email: '' });
        setError('');
        setCodeError('');
        setCodeStatus('idle');
        setSuccessMessage('');
    };

    const runAvailabilityCheck = async (code: string) => {
        try {
            const isUnique = isModal
                ? await PublicService.checkCodeUniqueness(code)
                : await InstitutionService.checkCodeUniqueness(code);
            setCodeStatus(isUnique ? 'available' : 'taken');
            setCodeError(isUnique ? '' : '✗ Already in use');
        } catch (err) {
            console.error('Error checking code uniqueness:', err);
            setCodeStatus('idle');
            setCodeError('Error checking code availability');
        }
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const code = e.target.value.toLowerCase();
        setInstitution((prev) => ({ ...prev, code }));

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (code === '') {
            setCodeError('');
            setCodeStatus('idle');
            return;
        }

        const formatError = validateCodeFormat(code);
        if (formatError) {
            setCodeError(formatError);
            setCodeStatus('idle');
            return;
        }

        setCodeError('');
        setCodeStatus('checking');
        debounceRef.current = setTimeout(() => runAvailabilityCheck(code), 350);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');
        setIsLoading(true);
        setSetupCreating(true);

        try {
            if (isModal) {
                // Public mode - create institution without authentication
                const chosen = PLAN_OPTIONS.find((p) => p.name === plan);
                const payload = chosen && chosen.name !== NOT_SURE_YET
                    ? { ...institution, description: `${institution.description}\n\nRequested plan: ${chosen.label}` }
                    : institution;
                await PublicService.createInstitution(payload);
                const message = 'Institution setup created successfully! Your onboarded institution login credentials will be sent later once institution is been approved.';
                setSuccessMessage(message);
                resetForm();
                if (onSuccess) onSuccess(message, institution.email);
            } else {
                // Authenticated mode
                await InstitutionService.createInstitution(institution);
                setSuccessMessage('Institution created successfully! Administrator credentials have been sent to the provided email.');
                navigate('/institutions');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create institution');
            console.error('Error creating institution:', err);
        } finally {
            setIsLoading(false);
            setSetupCreating(false);
        }
    };

    const createWhy = isLoading ? null
        : !institution.name ? 'Enter the institution name.'
        : !institution.code ? 'Enter the institution code.'
        : !isCodeValid ? 'The code is not confirmed as available yet. Fix the red note under the code box or wait for the check.'
        : !institution.description ? 'Enter the description.'
        : !institution.email ? 'Enter the admin e-mail address.'
        : null;

    const content = (
        <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
            <Paper sx={{ p: 3 }} data-help-page="institutions.create.page">
                <h2>Create New Institution</h2>
                {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label="Institution Name"
                        data-help="institutions.create.field.name"
                        value={institution.name}
                        onChange={(e) => setInstitution({ ...institution, name: e.target.value })}
                        margin="normal"
                        required
                    />
                    <TextField
                        fullWidth
                        label="Institution Code"
                        data-help="institutions.create.field.code"
                        value={institution.code}
                        onChange={handleCodeChange}
                        margin="normal"
                        required
                        error={codeStatus === 'taken' || (!!codeError && codeStatus === 'idle')}
                        helperText={
                            codeStatus === 'available'
                                ? <Box component="span" sx={{ color: 'success.main' }}>✓ Available</Box>
                                : codeStatus === 'checking'
                                    ? 'Checking availability…'
                                    : codeError
                        }
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        data-help="institutions.create.field.description"
                        value={institution.description}
                        onChange={(e) => setInstitution({ ...institution, description: e.target.value })}
                        margin="normal"
                        multiline
                        rows={3}
                        required
                    />
                    {isModal && (
                        <TextField
                            fullWidth
                            select
                            label="Pricing plan"
                            data-help="institutions.create.field.plan"
                            value={plan}
                            onChange={(e) => setPlan(e.target.value)}
                            margin="normal"
                            helperText="You can switch plans anytime — billing starts only after your institution is approved"
                        >
                            {PLAN_OPTIONS.map((option) => (
                                <MenuItem key={option.name} value={option.name}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        fullWidth
                        label="Admin Email Address (credentials will be sent here)"
                        data-help="institutions.create.field.email"
                        value={institution.email}
                        onChange={(e) => setInstitution({ ...institution, email: e.target.value })}
                        margin="normal"
                        type="email"
                        required
                        helperText="Institution setup details and admin credentials will be sent to this email after institution is being approved"
                    />

                    {setupCreating && (
                        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                            Your institution setup is being created. Please wait...
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            type="button"
                            onClick={() => {
                                if (isModal && onClose) {
                                    resetForm();
                                    onClose();
                                } else {
                                    navigate('/institutions');
                                }
                            }}
                            variant="outlined"
                            data-help="institutions.create.cancel"
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            data-help="institutions.create.save"
                            {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)}
                            disabled={!institution.name || !institution.code || !institution.description || !institution.email || !isCodeValid || isLoading}
                        >
                            {isLoading ? 'Creating...' : 'Create Institution'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );

    if (isModal) {
        const modalStyle = {
            position: 'absolute' as 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 1,
            borderRadius: 2
        };

        return (
            <Modal
                open={open}
                onClose={() => {
                    if (onClose) {
                        resetForm();
                        onClose();
                    }
                }}
            >
                <Box sx={modalStyle}>
                    {content}
                </Box>
            </Modal>
        );
    }

    // Regular page view
    return (
        <div style={{ padding: '20px' }}>
            {content}
        </div>
    );
};

export default CreateInstitution;
